import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  
  constructor() {
    super();
    // Cache for progress bar state to prevent 0% flash
    this.progressBarCache = {
      lastState: null,
      isValid: false
    };
  }
  
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartChange);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartChange);

    if (this.dialog && this.dialog.hasAttribute('open')) {
      this.updateProgressBar();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartChange);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartChange);
  }

  #handleCartChange = (event) => {
    if (this.hasAttribute('auto-open') && event.type === CartAddEvent.eventName) {
      this.showDialog();
    }
    this.updateProgressBar();
  };

  open() {
    this.showDialog();
    this.updateProgressBar();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // =================================================================
  // PROGRESS BAR LOGIC
  // =================================================================
  
  applyCachedProgressState() {
    if (!this.progressBarCache.isValid || !this.progressBarCache.lastState) {
      return false;
    }

    const wrapper = this.querySelector('#cpb-wrapper');
    const messageEl = this.querySelector('#cpb-message');
    const fillEl = this.querySelector('#cpb-fill');
    
    if (!wrapper || !messageEl || !fillEl) {
      return false;
    }

    const cached = this.progressBarCache.lastState;
    
    // Apply cached visual state immediately
    fillEl.style.width = cached.fillWidth;
    messageEl.innerHTML = cached.message;
    wrapper.classList.toggle('is-complete', cached.isComplete);
    
    // Apply cached icon states
    if (cached.iconStates) {
      cached.iconStates.forEach(iconState => {
        const iconGroup = this.querySelector(`#cpb-icon-group-${iconState.goalId}`);
        if (iconGroup) {
          iconGroup.style.left = iconState.position;
          iconGroup.classList.toggle('is-reached', iconState.isReached);
        }
      });
    }
    
    wrapper.classList.add('cpb-loaded');
    return true;
  }

  cacheProgressState(wrapper, messageEl, fillEl, iconStates) {
    this.progressBarCache.lastState = {
      fillWidth: fillEl.style.width,
      message: messageEl.innerHTML,
      isComplete: wrapper.classList.contains('is-complete'),
      iconStates: iconStates
    };
    this.progressBarCache.isValid = true;
  }

  updateProgressBar() {
    const self = this;
    const wrapper = this.querySelector('#cpb-wrapper');

    
    this.applyCachedProgressState();
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        self.updateCartHeader(cart);

        const currentWrapper = self.querySelector('#cpb-wrapper'); // Re-find the wrapper
        if (!cart || !cart.items || cart.items.length === 0) {
          if (currentWrapper) {
            currentWrapper.classList.remove('cpb-loaded');
            self.progressBarCache.isValid = false;
          }
          return;
        }
        
        const goalsDataEl = self.querySelector('#cpb-goals-data');
        if (currentWrapper && goalsDataEl) {
          self.calculateAndUpdateProgress(cart, currentWrapper, goalsDataEl);
          currentWrapper.classList.add('cpb-loaded');
        }
      })
      .catch(error => {
        console.error('Cart update failed:', error);
      })
      .finally(() => {
        
      });
  }

  updateCartHeader(cart) {
    const cartHeading = this.querySelector('.cart-drawer__heading');
    if (!cartHeading) {
      return;
    }

    const cartBubble = cartHeading.querySelector('.cart-bubble');
    const cartCount = cartBubble ? cartBubble.querySelector('.cart-count-bubble') : null;

    if (cart.item_count > 0) {
      // Find the text node to replace 'Your cart is empty' without removing the bubble span
      const headingTextNode = Array.from(cartHeading.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
      if (headingTextNode && headingTextNode.textContent.trim() !== "Cart") {
        headingTextNode.textContent = `Cart `;
      }
      
      if (cartBubble) cartBubble.style.display = 'inline-flex';
      if (cartCount) cartCount.textContent = cart.item_count;

    } else {
      // When empty, we can safely set the whole content
      cartHeading.innerHTML = `Your cart is empty`;
    }
  }

  calculateAndUpdateProgress(cart, wrapper, goalsDataEl) {
    const goals = JSON.parse(goalsDataEl.textContent || '[]');
    if (goals.length === 0) {
      return;
    }

    const globalBasis = wrapper.dataset.progressBasis; // 'item_count' or 'price'
    const moneyFormat = wrapper.dataset.moneyFormat;
    const excludedIds = (wrapper.dataset.excludedProductIds || '').split(',').filter(Boolean);
    
    // Calculate all metrics for dual basis support
    let currentMetrics = {
      itemCount: 0,
      subtotal: 0,
      total: 0
    };
    
    cart.items.forEach((item) => {
      const isExcluded = excludedIds.includes(item.product_id.toString());
      if (!isExcluded) {
        currentMetrics.itemCount += item.quantity;
        currentMetrics.subtotal += item.original_line_price;
        currentMetrics.total += item.final_line_price;
      }
    });
    
    // Find max threshold for percentage calculations
    const maxThreshold = Math.max(...goals.map(goal => goal.threshold));
    
    // Calculate fill percentage with dual basis support (NO MORE 94% REDUCTION)
    const fillPercentage = this.calculateFillToIconCenter(currentMetrics, goals, maxThreshold, globalBasis);
    
    const messageEl = this.querySelector('#cpb-message');
    const fillEl = this.querySelector('#cpb-fill');
    
    if (!messageEl || !fillEl) {
      return;
    }
    
    // Apply fill without 94% reduction - reaches icon centers when goals achieved
    fillEl.style.width = `${fillPercentage}%`;
    
    // Update goal messages and completion state
    this.updateGoalStates(currentMetrics, goals, globalBasis, moneyFormat, wrapper, messageEl);
    
    // Position icons dynamically with CSS fallback protection
    const iconStates = this.positionIconsWithFallback(goals, maxThreshold, currentMetrics, globalBasis);
    
    // Cache the current state
    this.cacheProgressState(wrapper, messageEl, fillEl, iconStates);
  }

  // Helper method for goal metric calculation (supports dual basis)
  calculateGoalMetric(goal, currentMetrics, globalBasis) {
    if (globalBasis === 'item_count') {
      return currentMetrics.itemCount;
    } else {
      // Price-based calculation
      if (goal.basis === 'subtotal') {
        return currentMetrics.subtotal;
      } else {
        return currentMetrics.total; // default to total
      }
    }
  }

  // Fill calculation to reach icon centers - DUAL BASIS SUPPORT
  calculateFillToIconCenter(currentMetrics, goals, maxThreshold, globalBasis) {
    // Find highest achieved goal across different basis types
    let highestAchievedGoal = null;
    let highestAchievedPosition = 0;
    
    for (const goal of goals.sort((a, b) => a.threshold - b.threshold)) {
      const goalMetric = this.calculateGoalMetric(goal, currentMetrics, globalBasis);
      
      if (goalMetric >= goal.threshold) {
        highestAchievedGoal = goal;
        highestAchievedPosition = (goal.threshold / maxThreshold) * 100;
      } else {
        break; // Stop at first unachieved goal
      }
    }
    
    if (highestAchievedGoal) {
      // Fill to center of highest achieved goal icon
      return highestAchievedPosition;
    } else {
      // Partial progress toward first unachieved goal
      const nextGoal = goals.find(goal => {
        const goalMetric = this.calculateGoalMetric(goal, currentMetrics, globalBasis);
        return goalMetric < goal.threshold;
      });
      
      if (nextGoal) {
        const currentProgress = this.calculateGoalMetric(nextGoal, currentMetrics, globalBasis);
        const progressPercentage = (currentProgress / nextGoal.threshold);
        const goalPosition = (nextGoal.threshold / maxThreshold) * 100;
        
        // Fill proportionally toward next goal, but stop before reaching icon
        return Math.min(progressPercentage * goalPosition, goalPosition * 0.9);
      }
    }
    
    return 0;
  }

  // Helper method for goal state updates
  updateGoalStates(currentMetrics, goals, globalBasis, moneyFormat, wrapper, messageEl) {
    // Find next unachieved goal
    const nextGoal = goals.find(goal => {
      const goalMetric = this.calculateGoalMetric(goal, currentMetrics, globalBasis);
      return goalMetric < goal.threshold;
    });
    
    if (nextGoal) {
      // Calculate remaining amount/items needed
      const currentProgress = this.calculateGoalMetric(nextGoal, currentMetrics, globalBasis);
      const remaining = nextGoal.threshold - currentProgress;
      
      // Format remaining based on basis type
      const remainingFormatted = globalBasis === 'item_count' ? 
        remaining.toString() : 
        this.formatCurrency(moneyFormat, remaining / 100);
      
      messageEl.innerHTML = nextGoal.pre_message.replace('[remaining]', remainingFormatted);
      wrapper.classList.remove('is-complete');
    } else {
      // All goals achieved
      const lastGoal = goals[goals.length - 1];
      messageEl.innerHTML = lastGoal.post_message || 'All goals achieved!';
      wrapper.classList.add('is-complete');
    }
  }

  // Helper method for icon positioning with fallback protection
  positionIconsWithFallback(goals, maxThreshold, currentMetrics, globalBasis) {
    const iconStates = [];
    
    goals.forEach((goal, index) => {
      const iconGroup = this.querySelector(`#cpb-icon-group-${goal.id}`);
      if (iconGroup) {
        // Calculate precise position based on actual threshold
        const precisePosition = (goal.threshold / maxThreshold) * 100;
        
        // Get current position (CSS fallback or previous JS setting)
        const currentStyle = iconGroup.style.left;
        const currentPosition = currentStyle ? 
          parseFloat(currentStyle) : 
          this.getCSSFallbackPosition(iconGroup);
        
        // Only override if significantly different (CSS should now be very precise, so tiny threshold)
        if (Math.abs(precisePosition - currentPosition) > 0.1) {
          iconGroup.style.left = `${precisePosition}%`;
        }
        
        // Update reached state based on current metrics
        const goalMetric = this.calculateGoalMetric(goal, currentMetrics, globalBasis);
        const isReached = goalMetric >= goal.threshold;
        iconGroup.classList.toggle('is-reached', isReached);
        
        // Store state for caching
        iconStates.push({
          goalId: goal.id,
          position: `${precisePosition}%`,
          isReached: isReached
        });
        
        // Update icon sources
        const defaultIcon = iconGroup.querySelector('.cpb-icon-default');
        const reachedIcon = iconGroup.querySelector('.cpb-icon-reached');
        if(defaultIcon) defaultIcon.src = goal.icon;
        if(reachedIcon) reachedIcon.src = goal.icon_reached;
      }
    });
    
    return iconStates;
  }

  // Helper method to get CSS fallback position
  getCSSFallbackPosition(iconGroup) {
    try {
      const computedStyle = getComputedStyle(iconGroup);
      const leftPx = parseFloat(computedStyle.left);
      const containerWidth = iconGroup.parentElement.offsetWidth;
      return containerWidth > 0 ? (leftPx / containerWidth) * 100 : 0;
    } catch (error) {
      // Fallback to 0 if calculation fails
      return 0;
    }
  }

  formatCurrency(format, value) {
    if (typeof value !== 'number' || !format) return '';
    return format.replace(/\{\{\s*amount\s*\}\}/, value.toFixed(2));
  }
}

customElements.define('cart-drawer-component', CartDrawerComponent);